import React, { useMemo, useState, useRef } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { Grid, Typography, IconButton, Menu, MenuItem, Box } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList
} from "recharts";

import SkeletonTotalGrowthBarChart from "../../../privateManagerApp/home/dashboard/ui-component/cards/Skeleton/TotalGrowthBarChart";

interface TotalGrowthBarChartProps {
  isLoading: boolean;
  dataCharDriverList: number[];
  dataChartVehiclesList: number[];
}

const months = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

const typographyPrometo = { fontFamily: "Prometo, sans-serif" };

const CustomTooltip = ({ active, payload, label, theme }: any) => {
  if (active && payload && payload.length) {
    return (
      <div 
        style={{ 
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)', 
          padding: '12px', 
          borderRadius: '8px', 
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: `1px solid ${theme.palette.divider}`,
          ...typographyPrometo
        }}
      >
        <Typography variant="subtitle2" style={{ ...typographyPrometo, fontWeight: 'bold', color: theme.palette.text.primary, marginBottom: 8 }}>
          {label}
        </Typography>
        {payload.map((entry: any, index: number) => (
          <Typography key={index} variant="body2" style={{ ...typographyPrometo, color: entry.fill, fontWeight: 500 }}>
            {entry.name}: {entry.value}
          </Typography>
        ))}
      </div>
    );
  }
  return null;
};

const TotalGrowthBarChart = ({
  isLoading,
  dataCharDriverList,
  dataChartVehiclesList,
}: TotalGrowthBarChartProps) => {
  const theme = useTheme();
  const chartRef = useRef<HTMLDivElement>(null);
  const [hiddenSeries, setHiddenSeries] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Tons dinâmicos atrelados ao tema, aplicando opacidade para suavizar (pastel nativo)
  const colorVeiculos = alpha(theme.palette.secondary.main || "#2196f3", 0.55); 
  const colorMotoristas = alpha(theme.palette.primary.main || "#673ab7", 0.55); 

  const formattedData = useMemo(() => {
    return months.map((month, index) => ({
      name: month,
      "Veículos": dataChartVehiclesList?.[index] || 0,
      "Motoristas": dataCharDriverList?.[index] || 0,
    }));
  }, [dataChartVehiclesList, dataCharDriverList]);

  const isEmpty = useMemo(() => {
    return !formattedData.some((d) => d["Veículos"] > 0 || d["Motoristas"] > 0);
  }, [formattedData]);

  const handleLegendClick = (name: string) => {
    if (hiddenSeries.includes(name)) {
      setHiddenSeries(hiddenSeries.filter((k) => k !== name));
    } else {
      setHiddenSeries([...hiddenSeries, name]);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const exportCSV = () => {
    const csvContent = [
      "Mês,Veículos,Motoristas",
      ...formattedData.map(d => `${d.name},${d["Veículos"]},${d["Motoristas"]}`)
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "chart_export.csv";
    link.click();
    handleMenuClose();
  };

  const exportSVG = () => {
    if (!chartRef.current) return;
    const svg = chartRef.current.querySelector(".recharts-wrapper svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "chart_export.svg";
    link.click();
    URL.revokeObjectURL(url);
    handleMenuClose();
  };

  const exportPNG = () => {
    if (!chartRef.current) return;
    const svg = chartRef.current.querySelector(".recharts-wrapper svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      if (ctx) {
        ctx.fillStyle = theme.palette.background.paper || "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        const link = document.createElement("a");
        link.download = "chart_export.png";
        link.href = canvas.toDataURL("image/png");
        link.click();
      }
    };
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    handleMenuClose();
  };

  if (isLoading) {
    return <SkeletonTotalGrowthBarChart />;
  }

  if (isEmpty) {
    return (
      <Grid className="dashboard-chart-surface tw-rounded-xl tw-bg-slate-50 dark:tw-bg-slate-900" container justifyContent="center" alignItems="center" style={{ minHeight: 350, background: "#f9fafb", borderRadius: 12, boxShadow: "none" }}>
        <Typography variant="h5" color="textSecondary" style={typographyPrometo}>
          Nenhum dado disponível para visualização.
        </Typography>
      </Grid>
    );
  }

  const renderInteractiveLegend = () => {
    const categories = [
      { name: "Veículos", color: colorVeiculos },
      { name: "Motoristas", color: colorMotoristas }
    ];
    return (
      <Box display="flex" justifyContent="center" flexWrap="wrap" gap={3} pt={2}>
        {categories.map((cat, index) => {
          const isHidden = hiddenSeries.includes(cat.name);
          return (
            <Box 
              key={index} 
              onClick={() => handleLegendClick(cat.name)}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', opacity: isHidden ? 0.5 : 1, transition: '0.2s' }}
            >
              <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: isHidden ? '#ccc' : cat.color, mr: 1 }} />
              <Typography variant="body2" sx={{ ...typographyPrometo, color: theme.palette.text.secondary, textDecoration: isHidden ? 'line-through' : 'none' }}>
                {cat.name}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderLabel = (props: any) => {
    const { x, y, width, height, value } = props;
    if (value <= 0) return null;
    return (
      <text x={x + width / 2} y={y + height / 2} fill={theme.palette.text.primary} dominantBaseline="middle" textAnchor="middle" style={{ ...typographyPrometo, fontSize: 11, fontWeight: 500 }}>
        {value}
      </text>
    );
  };

  return (
    <div className="dashboard-chart-surface tw-relative tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-overflow-hidden tw-rounded-xl tw-bg-slate-50 dark:tw-bg-slate-900" style={{ position: "relative", height: "clamp(350px, 40vw, 440px)", width: "100%", background: "#f9fafb", borderRadius: 12, outline: "none" }} ref={chartRef}>
      <style>{`.dashboard-chart-surface .recharts-wrapper, .dashboard-chart-surface .recharts-surface { outline: none !important; }`}</style>
      <IconButton className="!tw-bg-white/80 !tw-text-gtr-teal tw-shadow-sm hover:!tw-bg-white dark:!tw-bg-slate-800 dark:!tw-text-slate-100 dark:hover:!tw-bg-slate-700" onClick={handleMenuClick} size="small" style={{ position: "absolute", top: 8, right: 10, zIndex: 10 }}>
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
          <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
        </svg>
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={exportSVG} style={typographyPrometo}>Download SVG</MenuItem>
        <MenuItem onClick={exportPNG} style={typographyPrometo}>Download PNG</MenuItem>
        <MenuItem onClick={exportCSV} style={typographyPrometo}>Download CSV</MenuItem>
      </Menu>
      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, pt: 5 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={formattedData} margin={{ top: 20, right: 16, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
          <XAxis dataKey="name" interval="preserveStartEnd" minTickGap={12} tick={{ fill: theme.palette.text.secondary, fontSize: 11, ...typographyPrometo }} axisLine={{ stroke: theme.palette.divider }} tickLine={false} />
          <YAxis tick={{ fill: theme.palette.text.secondary, fontSize: 13, ...typographyPrometo }} axisLine={false} tickLine={false} />
          <Tooltip content={<CustomTooltip theme={theme} />} cursor={{ fill: theme.palette.action.hover, opacity: 0.15 }} />
          <Legend content={renderInteractiveLegend} />
          <Bar hide={hiddenSeries.includes("Veículos")} dataKey="Veículos" stackId="a" fill={colorVeiculos} animationDuration={600} maxBarSize={45}>
            <LabelList dataKey="Veículos" content={renderLabel} />
          </Bar>
          <Bar hide={hiddenSeries.includes("Motoristas")} dataKey="Motoristas" stackId="a" fill={colorMotoristas} animationDuration={600} maxBarSize={45} radius={[4, 4, 0, 0]}>
            <LabelList dataKey="Motoristas" content={renderLabel} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      </Box>
    </div>
  );
};

export default TotalGrowthBarChart;
